import { Helmet } from "react-helmet";
import Header from "../components/Header";
import SideNav from "../components/SideNav";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import {
  fetchReferralAnalytics,
  fetchReferrers,
  fetchReferralLeaderboard,
  fetchReferrerDetail,
  fetchReferralDetail,
  fetchReferralLink,
  fetchStaffLeaderboard,
  fetchReferralStates,
} from "../actions/referralActions";

// CSV download helper
const downloadCSV = (filename, headers, rows) => {
  const escape = (val) => {
    if (val === null || val === undefined) return "";
    const s = String(val);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const csv = [
    headers.join(","),
    ...rows.map((row) => row.map(escape).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Fallback colors for states (keyed by state id)
const STATE_COLORS = {
  1: "#ffc107", // Signed Up
  2: "#28a745", // KYC Completed
  3: "#007bff", // Wallet Funded
  4: "#6f42c1",
  5: "#dc3545",
};

const modalOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.55)",
  zIndex: 1000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
};

const modalBox = {
  borderRadius: "10px",
  width: "100%",
  maxWidth: "820px",
  maxHeight: "85vh",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
};

const modalHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "18px 24px",
};

const modalBody = {
  overflowY: "auto",
  padding: "24px",
};

const closeBtn = {
  background: "transparent",
  border: "none",
  fontSize: "22px",
  cursor: "pointer",
  color: "#aaa",
  lineHeight: 1,
};

const Referral = () => {
  const dispatch = useDispatch();
  const initRef = useRef(true);
  const [copyingTag, setCopyingTag] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [referrersPage, setReferrersPage] = useState(1);
  const [leaderboardPage, setLeaderboardPage] = useState(1);
  const [staffPage, setStaffPage] = useState(1);

  const [referrerModal, setReferrerModal] = useState(false);
  const [selectedReferrer, setSelectedReferrer] = useState(null);
  const [referralModal, setReferralModal] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);

  const {
    loading: analyticsLoading,
    data: analyticsData,
    error: analyticsError,
  } = useSelector((state) => state.getReferralAnalytics) || {};
  const {
    loading: referrersLoading,
    data: referrersData,
    error: referrersError,
  } = useSelector((state) => state.getReferralReferrers) || {};
  const {
    loading: leaderboardLoading,
    data: leaderboardData,
    error: leaderboardError,
  } = useSelector((state) => state.getReferralLeaderboard) || {};
  const {
    loading: staffLoading,
    data: staffData,
    error: staffError,
  } = useSelector((state) => state.getStaffLeaderboard) || {};
  const {
    loading: detailLoading,
    data: detailData,
    error: detailError,
  } = useSelector((state) => state.getReferrerDetail) || {};
  const {
    loading: referralDetailLoading,
    data: referralDetailData,
    error: referralDetailError,
  } = useSelector((state) => state.getReferralDetail) || {};
  const { data: statesData } =
    useSelector((state) => state.getReferralStates) || {};
  const {
    loading: linkLoading,
    data: linkData,
    error: linkError,
  } = useSelector((state) => state.getReferralLink) || {};

  // Build a quick lookup: { 1: "Signed Up", 2: "KYC Completed", ... }
  const stateLabels = (statesData?.data?.states || []).reduce((acc, s) => {
    acc[s.id] = { label: s.name, color: STATE_COLORS[s.id] || "#888" };
    return acc;
  }, {});

  useEffect(() => {
    if (initRef.current) {
      dispatch(fetchReferralAnalytics());
      dispatch(fetchReferrers(1, 10));
      dispatch(fetchReferralLeaderboard(1, 10));
      dispatch(fetchStaffLeaderboard(1, 10));
      dispatch(fetchReferralStates());
      initRef.current = false;
    }
  }, [dispatch]);

  const handleDateFilter = () => {
    if (fromDate && toDate && fromDate > toDate) {
      toast.error("'From' date cannot be after 'To' date");
      return;
    }
    dispatch(fetchReferralAnalytics(fromDate, toDate));
    dispatch(fetchReferrers(1, 10, fromDate, toDate));
    dispatch(fetchReferralLeaderboard(1, 10, fromDate, toDate));
    dispatch(fetchStaffLeaderboard(1, 10, fromDate, toDate));
    setReferrersPage(1);
    setLeaderboardPage(1);
    setStaffPage(1);
  };

  const handleReferrersPageChange = (page) => {
    setReferrersPage(page);
    dispatch(fetchReferrers(page, 10, fromDate, toDate));
  };

  const handleLeaderboardPageChange = (page) => {
    setLeaderboardPage(page);
    dispatch(fetchReferralLeaderboard(page, 10, fromDate, toDate));
  };

  const handleStaffPageChange = (page) => {
    setStaffPage(page);
    dispatch(fetchStaffLeaderboard(page, 10, fromDate, toDate));
  };

  const handleViewReferrer = (item) => {
    setSelectedReferrer(item);
    dispatch(fetchReferrerDetail(item.referrer_id));
    dispatch(fetchReferralLink(item.referrer_tag));
    setReferrerModal(true);
    document.body.style.overflow = "hidden";
  };

  const handleViewReferral = (item) => {
    setSelectedReferral(item);
    dispatch(fetchReferralDetail(item.referrer_id, item.referee_id));
    setReferralModal(true);
  };

  const closeReferrerModal = () => {
    setReferrerModal(false);
    setSelectedReferrer(null);
    setReferralModal(false);
    setSelectedReferral(null);
    document.body.style.overflow = "auto";
  };

  const closeReferralModal = () => {
    setReferralModal(false);
    setSelectedReferral(null);
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link).then(() => {
      toast.success("Referral link copied!");
    });
  };

  const handleCopyStaffLink = async (referrerTag) => {
    setCopyingTag(referrerTag);
    try {
      const response = await dispatch(fetchReferralLink(referrerTag));
      const link = response?.data?.referral_link;
      if (link) {
        await navigator.clipboard.writeText(link);
        toast.success("Referral link copied!");
      } else {
        toast.error("No referral link found");
      }
    } catch (err) {
      toast.error("Could not fetch referral link");
    } finally {
      setCopyingTag(null);
    }
  };

  const handleOpenStaffReferralLink = async (referrerTag) => {
    try {
      const response = await dispatch(fetchReferralLink(referrerTag));
      const link = response?.data?.referral_link;
      if (link) {
        window.open(link, "_blank", "noopener,noreferrer");
      } else {
        toast.error("No referral link found");
      }
    } catch (err) {
      toast.error("Could not fetch referral link");
    }
  };

  const handleDownloadReferrers = () => {
    if (!referrers?.items?.length) {
      toast.warning("No referrers data to download");
      return;
    }
    downloadCSV(
      "referrers",
      [
        "Position",
        "Referrer Tag",
        "Referrer Name",
        "Referral Count",
        "Referral Points",
      ],
      referrers.items.map((i) => [
        i.position,
        i.referrer_tag,
        i.referrer_name || "",
        i.referral_count,
        i.referral_points,
      ]),
    );
  };

  const handleDownloadLeaderboard = () => {
    if (!leaderboard?.items?.length) {
      toast.warning("No leaderboard data to download");
      return;
    }
    downloadCSV(
      "leaderboard",
      [
        "Position",
        "Referrer Tag",
        "Referrer Name",
        "Referral Count",
        "Referral Points",
      ],
      leaderboard.items.map((i) => [
        i.position,
        i.referrer_tag,
        i.referrer_name || "",
        i.referral_count,
        i.referral_points,
      ]),
    );
  };

  const handleDownloadStaff = () => {
    if (!staffLeaderboard?.items?.length) {
      toast.warning("No staff data to download");
      return;
    }
    downloadCSV(
      "staff_leaderboard",
      [
        "Position",
        "Referrer Tag",
        "Referrer Name",
        "Referral Count",
        "Referral Points",
      ],
      staffLeaderboard.items.map((i) => [
        i.position,
        i.referrer_tag,
        i.referrer_name || "",
        i.referral_count,
        i.referral_points,
      ]),
    );
  };

  // // Deep link to staff profile
  // const handleNavigateToStaff = (referrerId) => {
  //   // TODO: Update path to match your actual staff profile route
  //   navigate(`/admin/staff/${referrerId}`);
  // };

  const analytics = analyticsData?.data;
  const referrers = referrersData?.data?.referrals;
  const leaderboard = leaderboardData?.data?.referrals;
  const staffLeaderboard = staffData?.data?.referrals;
  const detail = detailData?.data;
  const referralDetail = referralDetailData?.data;
  const referralLink = linkData?.data?.referral_link;

  const downloadBtnStyle = {
    padding: "8px 16px",
    background: "#28a745",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "600",
  };

  return (
    <>
      <SideNav />
      <Helmet>
        <title>Admin | PennyTree - Referrals</title>
      </Helmet>
      <section className="page-container">
        <div className="page-content">
          <Header />
          <div className="page-body">
            <h1>Referral Management</h1>

            {/* Date Range Filter */}
            {/* Date Range Filter */}
            <div
              style={{
                display: "flex",
                gap: "10px",
                marginBottom: "20px",
                alignItems: "flex-start",
                flexWrap: "wrap",
              }}
            >
              <div className="form-group" style={{ margin: 0 }}>
                <label
                  style={{
                    fontSize: "13px",
                    marginBottom: "4px",
                    display: "block",
                  }}
                >
                  From:
                </label>
                <input
                  type="date"
                  className="invest-input"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={{ width: "180px", height: "42px" }}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label
                  style={{
                    fontSize: "13px",
                    marginBottom: "4px",
                    display: "block",
                  }}
                >
                  To:
                </label>
                <input
                  type="date"
                  className="invest-input"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={{ width: "180px", height: "42px" }}
                  min={fromDate || undefined}
                />
              </div>
              <div style={{ margin: 0 }}>
                {/* Invisible label spacer to match date inputs' label height */}
                <label
                  style={{
                    fontSize: "13px",
                    marginBottom: "4px",
                    display: "block",
                    visibility: "hidden",
                  }}
                >
                  .
                </label>
                <button
                  className="login-btn"
                  onClick={handleDateFilter}
                  style={{ padding: "0 20px", width: "100px", height: "42px" }}
                >
                  Apply
                </button>
              </div>
              {(fromDate || toDate) && (
                <div style={{ margin: 0 }}>
                  <label
                    style={{
                      fontSize: "13px",
                      marginBottom: "4px",
                      display: "block",
                      visibility: "hidden",
                    }}
                  >
                    .
                  </label>
                  <button
                    onClick={() => {
                      setFromDate("");
                      setToDate("");
                      dispatch(fetchReferralAnalytics());
                      dispatch(fetchReferrers(1, 10));
                      dispatch(fetchReferralLeaderboard(1, 10));
                      dispatch(fetchStaffLeaderboard(1, 10));
                      setReferrersPage(1);
                      setLeaderboardPage(1);
                      setStaffPage(1);
                    }}
                    style={{
                      padding: "0 16px",
                      height: "42px",
                      background: "transparent",
                      border: "1px solid #666",
                      borderRadius: "4px",
                      cursor: "pointer",
                      color: "#fff",
                      fontSize: "14px",
                    }}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>

            {/* Analytics Cards */}
            {analyticsLoading ? (
              <Loader />
            ) : analyticsError ? (
              <p className="text-danger text-center">{analyticsError}</p>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "16px",
                  marginBottom: "30px",
                }}
              >
                {[
                  {
                    label: "Total Referrals",
                    value: analytics?.total_referrals,
                    color: "#007bff",
                  },
                  {
                    label: "Successful",
                    value: analytics?.successful_referrals,
                    color: "#28a745",
                  },
                  {
                    label: "Pending",
                    value: analytics?.pending_referrals,
                    color: "#ffc107",
                  },
                  {
                    label: "Unique Referrers",
                    value: analytics?.unique_referrers,
                    color: "#6f42c1",
                  },
                ].map((card) => (
                  <div
                    key={card.label}
                    style={{
                      background: "#1a1a1a",
                      borderRadius: "8px",
                      padding: "20px",
                      borderLeft: `4px solid ${card.color}`,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    }}
                  >
                    <p style={{ fontSize: "13px", color: "#aaa", margin: 0 }}>
                      {card.label}
                    </p>
                    <h2
                      style={{
                        margin: "6px 0 0",
                        fontSize: "28px",
                        color: card.color,
                      }}
                    >
                      {card.value ?? "-"}
                    </h2>
                  </div>
                ))}
              </div>
            )}

            {/* Tabs */}
            <div
              style={{
                display: "flex",
                gap: "4px",
                marginBottom: "20px",
                borderBottom: "2px solid #333",
              }}
            >
              {[
                { key: "overview", label: "Referrers" },
                { key: "leaderboard", label: "Leaderboard" },
                { key: "staff", label: "Staff Leaderboard" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    padding: "10px 20px",
                    border: "none",
                    background:
                      activeTab === tab.key ? "#007bff" : "transparent",
                    color: activeTab === tab.key ? "#fff" : "#aaa",
                    borderRadius: "4px 4px 0 0",
                    cursor: "pointer",
                    fontWeight: activeTab === tab.key ? "600" : "400",
                    fontSize: "14px",
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Referrers Tab */}
            {activeTab === "overview" && (
              <>
                {referrersLoading ? (
                  <Loader />
                ) : referrersError ? (
                  <p className="text-danger text-center">{referrersError}</p>
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginBottom: "12px",
                      }}
                    >
                      <button
                        onClick={handleDownloadReferrers}
                        style={downloadBtnStyle}
                      >
                        ⬇ Download CSV
                      </button>
                    </div>
                    <MDBTable
                      responsive
                      style={{ width: "100%", fontSize: "14px" }}
                    >
                      <MDBTableHead>
                        <tr>
                          <th>#</th>
                          <th>Referrer Tag</th>
                          <th>Referrer Name</th>
                          <th>Referral Count</th>
                          <th>Referral Points</th>
                          <th>Action</th>
                        </tr>
                      </MDBTableHead>
                      <MDBTableBody>
                        {referrers?.items?.length > 0 ? (
                          referrers.items.map((item) => (
                            <tr key={item.referrer_id}>
                              <td>{item.position}</td>
                              <td>{item.referrer_tag}</td>
                              <td>{item.referrer_name || "-"}</td>
                              <td>{item.referral_count}</td>
                              <td>{item.referral_points}</td>
                              <td>
                                <p
                                  className="withdrawal-btn"
                                  onClick={() => handleViewReferrer(item)}
                                  style={{
                                    cursor: "pointer",
                                    color: "white",
                                    width: "80px",
                                    textAlign: "center",
                                    margin: 0,
                                  }}
                                >
                                  View
                                </p>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="6"
                              style={{ textAlign: "center", padding: "20px" }}
                            >
                              No referrers found
                            </td>
                          </tr>
                        )}
                      </MDBTableBody>
                    </MDBTable>

                    {referrers?.page?.total_page > 1 && (
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          justifyContent: "center",
                          marginTop: "16px",
                        }}
                      >
                        {Array.from(
                          { length: referrers.page.total_page },
                          (_, i) => i + 1,
                        ).map((page) => (
                          <button
                            key={page}
                            onClick={() => handleReferrersPageChange(page)}
                            style={{
                              padding: "6px 12px",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              background:
                                referrersPage === page ? "#007bff" : "#1a1a1a",
                              color: "#fff",
                              cursor: "pointer",
                            }}
                          >
                            {page}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Leaderboard Tab */}
            {activeTab === "leaderboard" && (
              <>
                {leaderboardLoading ? (
                  <Loader />
                ) : leaderboardError ? (
                  <p className="text-danger text-center">{leaderboardError}</p>
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginBottom: "12px",
                      }}
                    >
                      <button
                        onClick={handleDownloadLeaderboard}
                        style={downloadBtnStyle}
                      >
                        ⬇ Download CSV
                      </button>
                    </div>
                    <MDBTable
                      responsive
                      style={{ width: "100%", fontSize: "14px" }}
                    >
                      <MDBTableHead>
                        <tr>
                          <th>Position</th>
                          <th>Referrer Tag</th>
                          <th>Referrer Name</th>
                          <th>Referral Count</th>
                          <th>Referral Points</th>
                        </tr>
                      </MDBTableHead>
                      <MDBTableBody>
                        {leaderboard?.items?.length > 0 ? (
                          leaderboard.items.map((item) => (
                            <tr key={item.referrer_id}>
                              <td>
                                <span
                                  style={{
                                    fontWeight: "700",
                                    color:
                                      item.position === 1
                                        ? "#FFD700"
                                        : item.position === 2
                                          ? "#C0C0C0"
                                          : item.position === 3
                                            ? "#CD7F32"
                                            : "#fff",
                                  }}
                                >
                                  #{item.position}
                                </span>
                              </td>
                              <td>{item.referrer_tag}</td>
                              <td>{item.referrer_name || "-"}</td>
                              <td>{item.referral_count}</td>
                              <td>{item.referral_points}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="5"
                              style={{ textAlign: "center", padding: "20px" }}
                            >
                              No leaderboard data found
                            </td>
                          </tr>
                        )}
                      </MDBTableBody>
                    </MDBTable>

                    {leaderboard?.page?.total_page > 1 && (
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          justifyContent: "center",
                          marginTop: "16px",
                        }}
                      >
                        {Array.from(
                          { length: leaderboard.page.total_page },
                          (_, i) => i + 1,
                        ).map((page) => (
                          <button
                            key={page}
                            onClick={() => handleLeaderboardPageChange(page)}
                            style={{
                              padding: "6px 12px",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              background:
                                leaderboardPage === page
                                  ? "#007bff"
                                  : "#1a1a1a",
                              color: "#fff",
                              cursor: "pointer",
                            }}
                          >
                            {page}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Staff Leaderboard Tab */}
            {activeTab === "staff" && (
              <>
                {staffLoading ? (
                  <Loader />
                ) : staffError ? (
                  <p className="text-danger text-center">{staffError}</p>
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginBottom: "12px",
                      }}
                    >
                      <button
                        onClick={handleDownloadStaff}
                        style={downloadBtnStyle}
                      >
                        ⬇ Download CSV
                      </button>
                    </div>
                    <MDBTable
                      responsive
                      style={{ width: "100%", fontSize: "14px" }}
                    >
                      <MDBTableHead>
                        <tr>
                          <th>Position</th>
                          <th>Referrer Tag</th>
                          <th>Referrer Name</th>
                          <th>Referral Count</th>
                          <th>Referral Points</th>
                        </tr>
                      </MDBTableHead>
                      <MDBTableBody>
                        {staffLeaderboard?.items?.length > 0 ? (
                          staffLeaderboard.items.map((item) => (
                            <tr key={item.referrer_id}>
                              <td>
                                <span
                                  style={{
                                    fontWeight: "700",
                                    color:
                                      item.position === 1
                                        ? "#FFD700"
                                        : item.position === 2
                                          ? "#C0C0C0"
                                          : item.position === 3
                                            ? "#CD7F32"
                                            : "#fff",
                                  }}
                                >
                                  #{item.position}
                                </span>
                              </td>
                              <td>
                                <span
                                  onClick={() =>
                                    copyingTag !== item.referrer_tag &&
                                    handleCopyStaffLink(item.referrer_tag)
                                  }
                                  style={{
                                    color: "#4dabf7",
                                    textDecoration: "underline",
                                    fontWeight: "600",
                                    cursor:
                                      copyingTag === item.referrer_tag
                                        ? "wait"
                                        : "pointer",
                                    opacity:
                                      copyingTag === item.referrer_tag
                                        ? 0.6
                                        : 1,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                  title="Click to copy referral link"
                                >
                                  {item.referrer_tag}
                                  {copyingTag === item.referrer_tag && (
                                    <span
                                      style={{
                                        width: "12px",
                                        height: "12px",
                                        border: "2px solid #4dabf7",
                                        borderTopColor: "transparent",
                                        borderRadius: "50%",
                                        display: "inline-block",
                                        animation: "spin 0.6s linear infinite",
                                      }}
                                    />
                                  )}
                                </span>
                              </td>
                              <td>{item.referrer_name || "-"}</td>
                              <td>{item.referral_count}</td>
                              <td>{item.referral_points}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="4"
                              style={{ textAlign: "center", padding: "20px" }}
                            >
                              No staff leaderboard data found
                            </td>
                          </tr>
                        )}
                      </MDBTableBody>
                    </MDBTable>

                    {staffLeaderboard?.page?.total_page > 1 && (
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          justifyContent: "center",
                          marginTop: "16px",
                        }}
                      >
                        {Array.from(
                          { length: staffLeaderboard.page.total_page },
                          (_, i) => i + 1,
                        ).map((page) => (
                          <button
                            key={page}
                            onClick={() => handleStaffPageChange(page)}
                            style={{
                              padding: "6px 12px",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              background:
                                staffPage === page ? "#007bff" : "#1a1a1a",
                              color: "#fff",
                              cursor: "pointer",
                            }}
                          >
                            {page}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* ── MODAL 1: Referrer Detail ── */}
      {referrerModal && (
        <div style={modalOverlay} onClick={closeReferrerModal}>
          <div
            style={modalBox}
            className="referral-modal-box"
            onClick={(e) => e.stopPropagation()}
          >
            <div style={modalHeader} className="referral-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: "18px" }}>
                  Referrer Detail — {selectedReferrer?.referrer_tag}
                </h3>
                <p style={{ margin: "4px 0 0", fontSize: "13px" }}>
                  {selectedReferrer?.referrer_name &&
                    `${selectedReferrer.referrer_name} · `}
                  ID: {selectedReferrer?.referrer_id}
                </p>
              </div>
              <button style={closeBtn} onClick={closeReferrerModal}>
                ✕
              </button>
            </div>

            <div style={modalBody} className="referral-modal-body">
              {detailLoading ? (
                <Loader />
              ) : detailError ? (
                <p className="text-danger text-center">{detailError}</p>
              ) : detail ? (
                <>
                  {/* Referral Link */}
                  <div
                    style={{
                      background: "#2a2a2a",
                      borderRadius: "8px",
                      padding: "14px",
                      marginBottom: "20px",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <p
                        style={{
                          fontSize: "11px",
                          color: "#aaa",
                          margin: "0 0 4px",
                        }}
                      >
                        Referral Link
                      </p>
                      {linkLoading ? (
                        <span style={{ fontSize: "13px", color: "#888" }}>
                          Loading...
                        </span>
                      ) : linkError ? (
                        <span style={{ fontSize: "13px", color: "#dc3545" }}>
                          {linkError}
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: "13px",
                            color: "#fff",
                            wordBreak: "break-all",
                          }}
                        >
                          {referralLink || "-"}
                        </span>
                      )}
                    </div>
                    {referralLink && (
                      <button
                        onClick={() => handleCopyLink(referralLink)}
                        style={{
                          padding: "6px 14px",
                          background: "#007bff",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontWeight: "600",
                        }}
                      >
                        Copy
                      </button>
                    )}
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, 1fr)",
                      gap: "12px",
                      marginBottom: "24px",
                    }}
                  >
                    {[
                      {
                        label: "Total Referrals",
                        value: detail.overview?.total_referrals,
                        color: "#007bff",
                      },
                      {
                        label: "Completed",
                        value: detail.overview?.completed_referrals,
                        color: "#28a745",
                      },
                      {
                        label: "Pending",
                        value: detail.overview?.pending_referrals,
                        color: "#ffc107",
                      },
                      {
                        label: "Releasable Points",
                        value: detail.overview?.releasable_referral_points,
                        color: "#6f42c1",
                      },
                    ].map((card) => (
                      <div
                        key={card.label}
                        style={{
                          background: "#2a2a2a",
                          borderRadius: "8px",
                          padding: "14px",
                          borderLeft: `4px solid ${card.color}`,
                        }}
                      >
                        <p
                          style={{ fontSize: "11px", color: "#aaa", margin: 0 }}
                        >
                          {card.label}
                        </p>
                        <h4 style={{ margin: "4px 0 0", color: card.color }}>
                          {card.value ?? "-"}
                        </h4>
                      </div>
                    ))}
                  </div>

                  <p
                    style={{
                      fontWeight: "600",
                      fontSize: "14px",
                      marginBottom: "10px",
                    }}
                  >
                    Referrals List
                  </p>
                  <MDBTable responsive style={{ fontSize: "13px" }}>
                    <MDBTableHead>
                      <tr>
                        <th>Referee Name</th>
                        <th>Referee Tag</th>
                        <th>Points</th>
                        <th>Status</th>
                        <th>Credited</th>
                        <th>Action</th>
                      </tr>
                    </MDBTableHead>
                    <MDBTableBody>
                      {detail.referrals_list?.items?.length > 0 ? (
                        detail.referrals_list.items.map((item) => (
                          <tr key={`${item.referrer_id}-${item.referee_id}`}>
                            <td>{item.referee_name}</td>
                            <td>{item.referee_tag}</td>
                            <td>{item.referral_point}</td>
                            <td>
                              <span
                                style={{
                                  padding: "3px 10px",
                                  borderRadius: "12px",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  background: `${stateLabels[item.state]?.color || "#888"}33`,
                                  color:
                                    stateLabels[item.state]?.color || "#888",
                                }}
                              >
                                {stateLabels[item.state]?.label ?? item.state}
                              </span>
                            </td>
                            <td>
                              <span
                                style={{
                                  color: item.is_point_credited
                                    ? "#28a745"
                                    : "#dc3545",
                                  fontWeight: "600",
                                }}
                              >
                                {item.is_point_credited ? "Yes" : "No"}
                              </span>
                            </td>
                            <td>
                              <p
                                className="withdrawal-btn"
                                onClick={() => handleViewReferral(item)}
                                style={{
                                  cursor: "pointer",
                                  color: "white",
                                  width: "70px",
                                  textAlign: "center",
                                  fontSize: "12px",
                                }}
                              >
                                Details
                              </p>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="6"
                            style={{ textAlign: "center", padding: "20px" }}
                          >
                            No referrals found
                          </td>
                        </tr>
                      )}
                    </MDBTableBody>
                  </MDBTable>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL 2: Referral Detail ── */}
      {referralModal && (
        <div
          style={{ ...modalOverlay, zIndex: 1100 }}
          onClick={closeReferralModal}
        >
          <div
            style={{ ...modalBox, maxWidth: "520px" }}
            className="referral-modal-box"
            onClick={(e) => e.stopPropagation()}
          >
            <div style={modalHeader} className="referral-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: "18px" }}>Referral Detail</h3>
                <p style={{ margin: "4px 0 0", fontSize: "13px" }}>
                  {selectedReferral?.referrer_tag} →{" "}
                  {selectedReferral?.referee_tag}
                </p>
              </div>
              <button style={closeBtn} onClick={closeReferralModal}>
                ✕
              </button>
            </div>

            <div style={modalBody} className="referral-modal-body">
              {referralDetailLoading ? (
                <Loader />
              ) : referralDetailError ? (
                <p className="text-danger text-center">{referralDetailError}</p>
              ) : referralDetail ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "14px",
                  }}
                >
                  {[
                    {
                      label: "Referee Name",
                      value: referralDetail.referee_name,
                    },
                    { label: "Referee Tag", value: referralDetail.referee_tag },
                    {
                      label: "Referrer Tag",
                      value: referralDetail.referrer_tag,
                    },
                    {
                      label: "Referral Points",
                      value: referralDetail.referral_point,
                    },
                    {
                      label: "Status",
                      value: (
                        <span
                          style={{
                            padding: "3px 12px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "600",
                            background: `${stateLabels[referralDetail.state]?.color || "#888"}33`,
                            color:
                              stateLabels[referralDetail.state]?.color ||
                              "#888",
                          }}
                        >
                          {stateLabels[referralDetail.state]?.label ??
                            referralDetail.state}
                        </span>
                      ),
                    },
                    {
                      label: "Point Credited",
                      value: (
                        <span
                          style={{
                            color: referralDetail.is_point_credited
                              ? "#28a745"
                              : "#dc3545",
                            fontWeight: "600",
                          }}
                        >
                          {referralDetail.is_point_credited ? "Yes" : "No"}
                        </span>
                      ),
                    },
                  ].map((row) => (
                    <div
                      key={row.label}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        background: "#2a2a2a",
                        borderRadius: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "13px",
                          color: "#aaa",
                          fontWeight: "500",
                        }}
                      >
                        {row.label}
                      </span>
                      <span style={{ fontSize: "14px", fontWeight: "600" }}>
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Referral;
